import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { AppShell } from "@/components/AppShell";
import { CompanyAvatar } from "@/components/StatusBadge";
import {
  BOLAG,
  formatSEK,
  totalKapital,
  offentligaTyper,
  privataTyper,
  type FinansieringTyp,
} from "@/lib/portfolio-data";

export const Route = createFileRoute("/finansiering")({
  head: () => ({
    meta: [
      { title: "Finansiering — Inkubera" },
      { name: "description", content: "Finansieringsöversikt: privat och offentligt kapital över tid." },
    ],
  }),
  component: Finansiering,
});

function Finansiering() {
  const allFin = BOLAG.flatMap((b) => b.finansiering.map((f) => ({ ...f, bolag: b })));
  const totalt = allFin.reduce((s, f) => s + f.belopp, 0);
  const offentligt = allFin.filter((f) => offentligaTyper.includes(f.typ)).reduce((s, f) => s + f.belopp, 0);
  const privat = totalt - offentligt;

  const byTyp: Record<string, number> = {};
  allFin.forEach((f) => (byTyp[f.typ] = (byTyp[f.typ] || 0) + f.belopp));
  const typData = Object.entries(byTyp)
    .map(([typ, v]) => ({ typ, Mkr: +(v / 1_000_000).toFixed(1) }))
    .sort((a, b) => b.Mkr - a.Mkr);

  // Per år, stacked
  const yearMap: Record<string, Record<string, number>> = {};
  allFin.forEach((f) => {
    const y = String(new Date(f.datum).getFullYear());
    yearMap[y] = yearMap[y] || { Privat: 0, Offentligt: 0 };
    if (offentligaTyper.includes(f.typ)) yearMap[y].Offentligt += f.belopp / 1_000_000;
    else yearMap[y].Privat += f.belopp / 1_000_000;
  });
  const yearData = Object.entries(yearMap)
    .map(([år, v]) => ({ år, ...v }))
    .sort((a, b) => +a.år - +b.år);

  const senaste = [...allFin].sort((a, b) => +new Date(b.datum) - +new Date(a.datum)).slice(0, 8);

  const isOff = (t: FinansieringTyp) => offentligaTyper.includes(t);

  return (
    <AppShell>
      <section className="px-8 pt-10 pb-6 border-b border-border">
        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">Finansiering</div>
        <h1 className="font-display text-4xl">{formatSEK(totalt)} mobiliserat</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          {formatSEK(privat)} privat kapital och {formatSEK(offentligt)} offentligt stöd har flödat genom Inkuberas portfölj.
        </p>
      </section>

      <section className="px-8 py-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SplitCard label="Privat kapital" value={formatSEK(privat)} pct={(privat / totalt) * 100} color="var(--chart-1)" />
        <SplitCard label="Offentligt stöd" value={formatSEK(offentligt)} pct={(offentligt / totalt) * 100} color="var(--chart-2)" />
        <SplitCard label="Antal investeringar" value={String(allFin.length)} pct={100} color="var(--accent)" sub={`över ${BOLAG.filter((b) => b.finansiering.length > 0).length} bolag`} />
      </section>

      <section className="px-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-card border border-border p-5">
          <h3 className="font-display text-lg">Per år</h3>
          <p className="text-xs text-muted-foreground">Mkr, stackat</p>
          <div className="h-72 mt-3">
            <ResponsiveContainer>
              <BarChart data={yearData} margin={{ left: -16, right: 8, top: 8 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="år" stroke="var(--muted-foreground)" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--muted-foreground)" fontSize={12} tickLine={false} axisLine={false} unit=" Mkr" />
                <Tooltip contentStyle={{ background: "var(--popover)", border: "1px solid var(--border)", borderRadius: 12, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Offentligt" stackId="a" fill="var(--chart-2)" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Privat" stackId="a" fill="var(--chart-1)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl bg-card border border-border p-5">
          <h3 className="font-display text-lg">Per typ</h3>
          <p className="text-xs text-muted-foreground">Mkr, alla år</p>
          <div className="h-72 mt-3">
            <ResponsiveContainer>
              <BarChart data={typData} layout="vertical" margin={{ left: 30, right: 16, top: 8 }}>
                <CartesianGrid stroke="var(--border)" horizontal={false} />
                <XAxis type="number" stroke="var(--muted-foreground)" fontSize={12} tickLine={false} axisLine={false} unit=" Mkr" />
                <YAxis dataKey="typ" type="category" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} width={110} />
                <Tooltip contentStyle={{ background: "var(--popover)", border: "1px solid var(--border)", borderRadius: 12, fontSize: 12 }} />
                <Bar dataKey="Mkr" fill="var(--foreground)" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section className="px-8 py-8">
        <div className="rounded-2xl bg-card border border-border">
          <div className="p-5 border-b border-border">
            <h3 className="font-display text-lg">Senaste investeringar</h3>
          </div>
          <ul className="divide-y divide-border">
            {senaste.map((f, i) => (
              <li key={i} className="p-4 px-5 flex items-center gap-4">
                <CompanyAvatar initialer={f.bolag.initialer} farg={f.bolag.logoFarg} size={36} />
                <div className="flex-1 min-w-0">
                  <Link to="/bolag/$id" params={{ id: f.bolag.id }} className="font-medium hover:underline">
                    {f.bolag.namn}
                  </Link>
                  <div className="text-xs text-muted-foreground">
                    {f.investerare ?? f.typ} · {new Date(f.datum).toLocaleDateString("sv-SE")}
                  </div>
                </div>
                <span
                  className="text-[11px] px-2 py-0.5 rounded-full"
                  style={{
                    background: isOff(f.typ) ? "color-mix(in oklab, var(--chart-2) 18%, transparent)" : "color-mix(in oklab, var(--chart-1) 18%, transparent)",
                    color: isOff(f.typ) ? "var(--chart-2)" : "var(--chart-1)",
                  }}
                >
                  {f.typ}
                </span>
                <div className="font-display text-lg w-28 text-right">{formatSEK(f.belopp)}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="px-8 pb-12 text-xs text-muted-foreground">
        Privata typer: {privataTyper.join(", ")} · Offentliga: {offentligaTyper.join(", ")}
      </section>
    </AppShell>
  );
}

function SplitCard({ label, value, pct, color, sub }: { label: string; value: string; pct: number; color: string; sub?: string }) {
  return (
    <div className="p-5 rounded-2xl bg-card border border-border">
      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="font-display text-4xl mt-3">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{sub ?? `${pct.toFixed(0)}% av total`}</div>
      <div className="h-1.5 rounded-full bg-secondary overflow-hidden mt-4">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}
